import React from "react";
import { motion } from "framer-motion";
import { Box, Typography, Button, Paper } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";

export type CategoryListToolbarProps = {
  title?: string;
  onOpenFilters?: (event: React.MouseEvent<HTMLElement>) => void;
  onAddCategory: () => void;
  children?: React.ReactNode;
  isMobile?: boolean;
  filtersActiveCount?: number;
};

const CategoryListToolbar: React.FC<CategoryListToolbarProps> = ({
  title,
  onOpenFilters,
  onAddCategory,
  children,
  isMobile = false,
  filtersActiveCount = 0
}) => {
  const { t } = useKivunovaTranslation();

  const resolvedTitle = title ?? t("category-list", "Category list");
  const filtersLabel = `${t("filters", "Filters")} (${filtersActiveCount} ${t(
    filtersActiveCount !== 1 ? "active-filters" : "active-filter",
    filtersActiveCount !== 1 ? "active filters" : "active filter"
  )})`;

  return (
    <Paper
      component={motion.div}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper"
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        flexDirection={isMobile ? "column" : "row"}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              borderRadius: 1.5,
              backgroundColor: `${BRAND_COLOR}14`
            }}
          >
            <CategoryOutlinedIcon sx={{ color: BRAND_COLOR, fontSize: 22 }} />
          </Box>
          <Typography variant="h5" fontWeight="700" color="text.primary">
            {resolvedTitle}
          </Typography>
        </Box>

        <Box
          display="flex"
          alignItems="center"
          gap={1.5}
          flexDirection={isMobile ? "column" : "row"}
          flexWrap="wrap"
        >
          {onOpenFilters && (
            <Button
              variant="outlined"
              size="medium"
              startIcon={<FilterListIcon data-testid="FilterListIcon" />}
              onClick={onOpenFilters}
              aria-haspopup="true"
              aria-label={filtersLabel}
              sx={{
                borderColor: "divider",
                color: "text.secondary",
                textTransform: "none",
                fontWeight: 500,
                "&:hover": {
                  borderColor: BRAND_COLOR,
                  color: BRAND_COLOR,
                  backgroundColor: `${BRAND_COLOR}08`
                }
              }}
            >
              {t("filters", "Filters")}
            </Button>
          )}
          <Button
            variant="contained"
            size="medium"
            onClick={onAddCategory}
            sx={{
              backgroundColor: BRAND_COLOR,
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: BRAND_HOVER,
                boxShadow: "none"
              }
            }}
          >
            {t("add-new-category", "Add New Category")}
          </Button>
          {children}
        </Box>
      </Box>
    </Paper>
  );
};

export default CategoryListToolbar;
